import { Modal, Button } from "antd";
import { useMemo, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const safeNumber = (v) => {
  // Handle Mongo Decimal objects too
  if (v && typeof v === "object" && v.$numberDecimal) return Number(v.$numberDecimal);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const PrintPosPeriod = ({
  isModalOpen,
  setIsModalOpen,
  periodLabel,
  fromISO,
  toISO,
  invoices = [],
}) => {
  const componentRef = useRef(null);
  const user = useSelector((state) => state.user);

  const totals = useMemo(() => {
    const sum = invoices.reduce(
      (acc, inv) => {
        acc.count += 1;
        acc.subTotal += safeNumber(inv?.subTotal);
        acc.tax += safeNumber(inv?.tax);
        acc.totalAmount += safeNumber(inv?.totalAmount);
        return acc;
      },
      { count: 0, subTotal: 0, tax: 0, totalAmount: 0 }
    );
    return sum;
  }, [invoices]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `${periodLabel || "POS"}-${dayjs(fromISO).format("YYYY-MM-DD")}-to-${dayjs(toISO).format("YYYY-MM-DD")}`,
    pageStyle: `
      @page { size: A4; margin: 10mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `,
  });

  const fromText = fromISO ? dayjs(fromISO).format("YYYY-MM-DD") : "-";
  const toText = toISO ? dayjs(toISO).format("YYYY-MM-DD") : "-";

  return (
    <Modal
      title={periodLabel || "POS Period Invoice"}
      open={isModalOpen}
      footer={false}
      onCancel={() => setIsModalOpen(false)}
      width={950}
      destroyOnHidden
    >
      <div
        ref={componentRef}
        style={{
          width: "210mm",
          minHeight: "297mm",
          padding: "10mm",
          background: "#fff",
          color: "#000",
          fontFamily: "Arial, sans-serif",
          fontSize: 12,
          margin: "0 auto",
          border: "1px solid #000",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{user?.businessName || "Business"}</div>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{periodLabel || "POS Period Invoice"}</div>
          </div>
          <div style={{ textAlign: "right", fontSize: 12 }}>
            <div>
              <b>From:</b> {fromText}
            </div>
            <div>
              <b>To:</b> {toText}
            </div>
            <div>
              <b>Generated:</b> {dayjs().format("YYYY-MM-DD HH:mm")}
            </div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #000", marginBottom: 12 }} />

        <div style={{ marginBottom: 10, display: "flex", justifyContent: "space-between" }}>
          <div>
            <b>Total Invoices:</b> {totals.count}
          </div>
          <div>
            <b>SubTotal:</b> {totals.subTotal.toFixed(2)} &nbsp;|&nbsp; <b>Tax:</b>{" "}
            {totals.tax.toFixed(2)} &nbsp;|&nbsp; <b>Total:</b> {totals.totalAmount.toFixed(2)}
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f2f2f2" }}>
              {["#", "Invoice No", "Date/Time", "Payment", "Customer", "Total"].map((h) => (
                <th
                  key={h}
                  style={{
                    border: "1px solid #000",
                    padding: "6px 6px",
                    textAlign: h === "Total" ? "right" : "left",
                    fontSize: 12,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ border: "1px solid #000", padding: 10 }}>
                  No invoices found for this period.
                </td>
              </tr>
            ) : (
              invoices.map((inv, idx) => (
                <tr key={inv?._id || idx}>
                  <td style={{ border: "1px solid #000", padding: 6 }}>{idx + 1}</td>
                  <td style={{ border: "1px solid #000", padding: 6 }}>{inv?.invoiceNumber || "NA"}</td>
                  <td style={{ border: "1px solid #000", padding: 6 }}>
                    {inv?.createdAt ? new Date(inv.createdAt).toLocaleString("en-PK") : "NA"}
                  </td>
                  <td style={{ border: "1px solid #000", padding: 6 }}>{inv?.paymentMode || "NA"}</td>
                  <td style={{ border: "1px solid #000", padding: 6 }}>{inv?.customerName || "NA"}</td>
                  <td style={{ border: "1px solid #000", padding: 6, textAlign: "right" }}>
                    {safeNumber(inv?.totalAmount).toFixed(2)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end mt-4">
        <Button type="primary" onClick={handlePrint}>
          Print
        </Button>
      </div>
    </Modal>
  );
};

export default PrintPosPeriod;

